// src/components/forms/bodyMap/bodyMapArtwork.ts
// Copied from help-from-sky-native (same path). Keep the two in sync.
// SVG path data for the body map diagram. Pure TypeScript with no React Native
// imports so the booking site can copy this file as-is.
//
// Coordinates are in a 200 × 440 viewBox. Paired regions are drawn once for the
// screen-left side and mirrored. Left/right in region IDs mean the client's own
// side: on the front view the client's right is on the screen's left; on the
// back view their left is on the screen's left.

export const BODY_MAP_VIEWBOX = { width: 200, height: 440 } as const;

type Point = readonly [number, number];

/** Closed path through `points` with each corner rounded by up to `radius`. */
function roundedPolygon(points: readonly Point[], radius: number): string {
  const n = points.length;
  const parts: string[] = [];
  const fmt = (v: number) => String(Math.round(v * 10) / 10);
  for (let i = 0; i < n; i++) {
    const [px, py] = points[(i - 1 + n) % n];
    const [cx, cy] = points[i];
    const [nx, ny] = points[(i + 1) % n];
    const toPrev = Math.hypot(px - cx, py - cy);
    const toNext = Math.hypot(nx - cx, ny - cy);
    const r = Math.min(radius, toPrev / 2, toNext / 2);
    const ax = cx + ((px - cx) / toPrev) * r;
    const ay = cy + ((py - cy) / toPrev) * r;
    const bx = cx + ((nx - cx) / toNext) * r;
    const by = cy + ((ny - cy) / toNext) * r;
    parts.push(`${i === 0 ? 'M' : 'L'}${fmt(ax)} ${fmt(ay)}`, `Q${fmt(cx)} ${fmt(cy)} ${fmt(bx)} ${fmt(by)}`);
  }
  return `${parts.join(' ')} Z`;
}

const mirror = (points: readonly Point[]): Point[] =>
  points.map(([x, y]) => [BODY_MAP_VIEWBOX.width - x, y] as const);

const HEAD = 'M79 34 A21 25 0 1 0 121 34 A21 25 0 1 0 79 34 Z';
const NECK: Point[] = [[90, 52], [110, 52], [113, 79], [87, 79]];

// Screen-left shapes shared by both views.
const UPPER_ARM: Point[] = [[48, 113], [69, 113], [65, 172], [44, 172]];
const FOREARM: Point[] = [[44, 172], [65, 172], [58, 232], [39, 232]];
const HAND: Point[] = [[39, 232], [58, 232], [60, 250], [53, 268], [42, 267], [35, 250]];

// Front, screen-left.
const SHOULDER: Point[] = [[87, 79], [67, 80], [54, 87], [48, 102], [48, 113], [69, 113], [72, 99], [87, 91]];
const THIGH: Point[] = [[72, 225], [100, 236], [99, 318], [79, 318], [73, 272]];
const KNEE: Point[] = [[79, 318], [99, 318], [98, 345], [80, 345]];
const LOWER_LEG: Point[] = [[80, 345], [98, 345], [96, 410], [84, 410], [77, 372]];
const FOOT_FRONT: Point[] = [[84, 410], [96, 410], [98, 428], [94, 434], [76, 434], [75, 427]];
const CHEST: Point[] = [[87, 79], [113, 79], [113, 91], [128, 99], [131, 113], [127, 148], [73, 148], [69, 113], [72, 99], [87, 91]];
const ABDOMEN: Point[] = [[73, 148], [127, 148], [124, 196], [76, 196]];
const HIPS: Point[] = [[76, 196], [124, 196], [128, 225], [100, 236], [72, 225]];

// Back, screen-left.
const SHOULDER_BLADE: Point[] = [[87, 79], [67, 80], [54, 87], [48, 102], [48, 113], [69, 113], [71, 140], [100, 140], [100, 116], [74, 104], [87, 91]];
const UPPER_BACK: Point[] = [[87, 79], [113, 79], [113, 91], [126, 104], [100, 116], [74, 104], [87, 91]];
const MID_BACK: Point[] = [[71, 140], [129, 140], [127, 172], [73, 172]];
const LOWER_BACK: Point[] = [[73, 172], [127, 172], [125, 200], [75, 200]];
const GLUTE: Point[] = [[75, 200], [100, 200], [100, 244], [87, 250], [73, 244], [72, 222]];
const HAMSTRING: Point[] = [[73, 244], [87, 250], [100, 244], [99, 322], [79, 322], [74, 282]];
const CALF: Point[] = [[79, 322], [99, 322], [97, 410], [84, 410], [76, 366]];
const FOOT_BACK: Point[] = [[84, 410], [97, 410], [97, 428], [92, 434], [82, 434], [79, 426]];

/** Adds `left_<name>` / `right_<name>` from one screen-left shape. */
function pair(
  regions: Record<string, string>,
  name: string,
  screenLeft: readonly Point[],
  screenLeftSide: 'left' | 'right',
  radius: number,
): void {
  const otherSide = screenLeftSide === 'left' ? 'right' : 'left';
  regions[`${screenLeftSide}_${name}`] = roundedPolygon(screenLeft, radius);
  regions[`${otherSide}_${name}`] = roundedPolygon(mirror(screenLeft), radius);
}

function buildFront(): Record<string, string> {
  // On the front view the client's right is on the screen's left.
  const r: Record<string, string> = {
    neck: roundedPolygon(NECK, 3),
    head: HEAD,
    chest: roundedPolygon(CHEST, 4),
    abdomen: roundedPolygon(ABDOMEN, 4),
    hips: roundedPolygon(HIPS, 5),
  };
  pair(r, 'shoulder', SHOULDER, 'right', 5);
  pair(r, 'upper_arm', UPPER_ARM, 'right', 6);
  pair(r, 'forearm', FOREARM, 'right', 6);
  pair(r, 'hand', HAND, 'right', 5);
  pair(r, 'thigh', THIGH, 'right', 6);
  pair(r, 'knee', KNEE, 'right', 6);
  pair(r, 'lower_leg', LOWER_LEG, 'right', 5);
  pair(r, 'foot', FOOT_FRONT, 'right', 4);
  return r;
}

function buildBack(): Record<string, string> {
  // On the back view the client's left is on the screen's left.
  const r: Record<string, string> = {
    neck: roundedPolygon(NECK, 3),
    head: HEAD,
    upper_back: roundedPolygon(UPPER_BACK, 4),
    mid_back: roundedPolygon(MID_BACK, 4),
    lower_back: roundedPolygon(LOWER_BACK, 4),
  };
  pair(r, 'shoulder_blade', SHOULDER_BLADE, 'left', 5);
  pair(r, 'upper_arm', UPPER_ARM, 'left', 6);
  pair(r, 'forearm', FOREARM, 'left', 6);
  pair(r, 'hand', HAND, 'left', 5);
  pair(r, 'glute', GLUTE, 'left', 6);
  pair(r, 'hamstring', HAMSTRING, 'left', 6);
  pair(r, 'calf', CALF, 'left', 5);
  pair(r, 'foot', FOOT_BACK, 'left', 4);
  return r;
}

/** Region ID → SVG path `d`, per view. Draw in object order (neck before head). */
export const BODY_MAP_PATHS = {
  front: buildFront(),
  back: buildBack(),
} as const;
